import { memo } from "react";
import { Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";

import { cn } from "@/lib/utils";
import { codeMd } from "@/lib/format";
import type { CodeTimelineItem } from "@/hooks/useChat";
import { CopyButton } from "../../buttons/CopyButton";
import { MessageFrame } from "./MessageFrame";

const CodeRunningIndicator = () => (
  <div className="flex items-center gap-1.5">
    <Loader2 className="h-3 w-3 animate-spin text-emerald-500/60" />
    <span className="text-[10px] text-emerald-500/60">running...</span>
  </div>
);

const CodeOutput = ({
  output,
  isError,
}: {
  output: string;
  isError: boolean;
}) => (
  <pre className="overflow-x-auto">
    <code
      className={cn(
        "block whitespace-pre font-mono text-[11px] leading-[1.6]",
        isError ? "text-red-400/80" : "text-emerald-400/70",
      )}
    >
      {output}
    </code>
  </pre>
);

export const CodeCard = memo(function CodeCard({
  item,
}: {
  item: CodeTimelineItem;
}) {
  const copyText = item.output
    ? `${item.code}\n\n${item.output}`
    : item.code;
  const hasOutput = item.output || item.status === "running";

  return (
    <MessageFrame side="bot" wide>
      <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-white/[0.06] bg-zinc-900/80">
        <div className="flex items-center justify-between px-3 py-1">
          <span className="text-[10px] font-medium text-zinc-500">
            python
          </span>
          <CopyButton text={copyText} />
        </div>

        {item.code && (
          <div className="[&_pre]:overflow-x-auto [&_pre]:px-3 [&_pre]:pb-2 [&_pre]:m-0 [&_pre]:bg-transparent [&_code]:block [&_code]:whitespace-pre [&_code]:font-mono [&_code]:text-[11px] [&_code]:leading-[1.6] [&_code]:text-zinc-300">
            <ReactMarkdown rehypePlugins={[rehypeHighlight]}>
              {codeMd(item.code)}
            </ReactMarkdown>
          </div>
        )}

        {hasOutput && (
          <>
            <div className="mx-3 border-t border-emerald-500/15" />
            <div className="px-3 py-2">
              {item.status === "running" && !item.output && (
                <CodeRunningIndicator />
              )}
              {item.output && (
                <CodeOutput
                  output={item.output}
                  isError={item.status === "error"}
                />
              )}
            </div>
          </>
        )}
      </div>
    </MessageFrame>
  );
});
